import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, styled } from "@mui/material";
import type { PDFViewerProps } from "../../../components";
import { PDFViewer, SecondaryActionButton } from "../../../components";
import type { CompoundedPrescriptionPayload, DocumentProps } from "../../../interfaces";
import { PrescriptionStatus, requestTypeExtendedTranslation } from "../../../interfaces";
import { useGetPDFs } from "../../../hooks";
import { generatePdfName } from "../../../utils";
import { PrescriptionStatusLabel } from "@components/PrescriptionStatusLabel";
import { CompoundPrescriptionDetailCard } from "@screens/PrescriptionForm/components/CompoundPrescriptionDetailCard";

const DetailsWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  padding: "8px 0px",
  gap: "8px",
});

const Row = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
});

const DetailsTitle = styled(Typography)({
  fontWeight: 600,
  display: "flex",
  gap: "8px",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "8px",
  alignItems: "center",
});

const ShowDocumentButton = styled(SecondaryActionButton)({
  padding: "4px",
});

interface Props {
  data: CompoundedPrescriptionPayload;
  isFetching: boolean;
}

export const HistoricCompoundedPrescription = ({ data, isFetching }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    id: requestId,
    patient,
    documentType,
    documents: compoundedPrescriptions,
  } = data;

  const {
    id: documentId,
    details,
    prescriptionStatus,
    holdingDate,
    type: prescriptionType,
  } = compoundedPrescriptions[0];

  const [documentData, setDocumentData] = useState<DocumentProps>({} as DocumentProps);

  const {
    data: pdfData,
    isPending,
    isSuccess,
    isError,
  } = useGetPDFs({
    requestId,
  });

  const onClose = () => setDocumentData({} as DocumentProps);

  if (Object.keys(documentData).length !== 0) {
    const prevRoute = location.pathname;

    if (isPending) {
      return <PDFViewer.Skeleton prevRoute={prevRoute} />;
    } // TODO: If isError, maybe add a modal with an error message
    else if (isError) {
      onClose();
    } else if (isSuccess) {
      const treatmentPdfUrl = pdfData?.treatment?.url;

      const documentUrl = treatmentPdfUrl ?? pdfData?.documents?.[0]?.url;

      const navigationState: PDFViewerProps = {
        documentProps: {
          ...documentData,
          documentUrl,
        },
        pdfRenderingProps: {
          title: requestTypeExtendedTranslation[documentType],
          prevRoute,
        },
        prescriptionData: {
          requestId,
          prescriptionId: documentId,
          prescriptionStatus,
          holdingDate,
          isFetching,
          type: prescriptionType,
        },
      };

      navigate("/app/solicitudes/visualizacion-documento", {
        state: navigationState,
      });
    }
  }

  return (
    <DetailsWrapper>
      <Row>
        <DetailsTitle>Receta magistral</DetailsTitle>
        <ButtonContainer>
          {prescriptionStatus !== PrescriptionStatus.ISSUED && (
            <PrescriptionStatusLabel status={prescriptionStatus} />
          )}
          <ShowDocumentButton
            onClick={() =>
              setDocumentData({
                ...documentData,
                documentId: documentId,
                documentName: generatePdfName({
                  documentType,
                  patient,
                }),
              })
            }
            disabled={isError}
          >
            Ver documento
          </ShowDocumentButton>
        </ButtonContainer>
      </Row>
      <CompoundPrescriptionDetailCard details={details} />
    </DetailsWrapper>
  );
};

HistoricCompoundedPrescription.Skeleton =
  function HistoricCompoundedPrescriptionSkeleton() {
    return (
      <DetailsWrapper>
        <Row>
          <DetailsTitle>Receta magistral</DetailsTitle>
        </Row>
        <CompoundPrescriptionDetailCard.Skeleton />
      </DetailsWrapper>
    );
  };
